import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import SortableList from "@/components/SortableList";

function CategoryDialog({ open, onOpenChange, orgId, category, onSaved }) {
  const isEdit = !!category;
  const [form, setForm] = useState({ name: "", type: "income" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (category) setForm({ name: category.name, type: category.type });
    else if (open) setForm({ name: "", type: "income" });
  }, [category, open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/organizations/${orgId}/categories/${category.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post(`/organizations/${orgId}/categories`, form);
        toast.success("Category added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-dialog" className="rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Rename category" : "New category"}</DialogTitle>
          <DialogDescription>Categories organize your income and expense transactions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="category-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              data-testid="category-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Youth Ministry"
              className="rounded-xl"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={submitting} data-testid="category-save-button" className="rounded-xl">
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CategoriesPage({ embedded = false }) {
  const { activeOrgId } = useAuth();
  const [cats, setCats] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [editingFund, setEditingFund] = useState(null);
  const [confirmFund, setConfirmFund] = useState(null);
  const [fundName, setFundName] = useState("");
  const [origins, setOrigins] = useState([]);
  const [originDialogOpen, setOriginDialogOpen] = useState(false);
  const [editingOrigin, setEditingOrigin] = useState(null);
  const [confirmOrigin, setConfirmOrigin] = useState(null);
  const [originName, setOriginName] = useState("");

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [c, f, o] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/categories`),
        api.get(`/organizations/${activeOrgId}/funds`),
        api.get(`/organizations/${activeOrgId}/donation-origins`),
      ]);
      setCats(c.data);
      setFunds(f.data);
      setOrigins(o.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const doDelete = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/categories/${confirmTarget.id}`);
      toast.success(`"${confirmTarget.name}" deleted`);
      setConfirmTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmTarget(null);
    }
  };

  const openFundDialog = (f) => {
    setEditingFund(f);
    setFundName(f?.name || "");
    setFundDialogOpen(true);
  };

  const saveFund = async () => {
    if (!fundName.trim()) return toast.error("Name required");
    try {
      if (editingFund) {
        await api.patch(`/organizations/${activeOrgId}/funds/${editingFund.id}`, { name: fundName.trim() });
        toast.success("Fund updated");
      } else {
        await api.post(`/organizations/${activeOrgId}/funds`, { name: fundName.trim() });
        toast.success("Fund added");
      }
      setFundDialogOpen(false);
      setFundName("");
      setEditingFund(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDeleteFund = async () => {
    if (!confirmFund) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/funds/${confirmFund.id}`);
      toast.success(`"${confirmFund.name}" deleted`);
      setConfirmFund(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmFund(null);
    }
  };

  const openOriginDialog = (o) => {
    setEditingOrigin(o);
    setOriginName(o?.name || "");
    setOriginDialogOpen(true);
  };

  const saveOrigin = async () => {
    if (!originName.trim()) return toast.error("Name required");
    try {
      if (editingOrigin) {
        await api.patch(`/organizations/${activeOrgId}/donation-origins/${editingOrigin.id}`, { name: originName.trim() });
        toast.success("Origin updated");
      } else {
        await api.post(`/organizations/${activeOrgId}/donation-origins`, { name: originName.trim() });
        toast.success("Origin added");
      }
      setOriginDialogOpen(false);
      setOriginName("");
      setEditingOrigin(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDeleteOrigin = async () => {
    if (!confirmOrigin) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/donation-origins/${confirmOrigin.id}`);
      toast.success(`"${confirmOrigin.name}" deleted`);
      setConfirmOrigin(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmOrigin(null);
    }
  };

  const moveOrigin = async (newOrder) => {
    setOrigins(newOrder);
    try {
      await api.post(`/organizations/${activeOrgId}/donation-origins/reorder`, {
        ordered_ids: newOrder.map((o) => o.id),
      });
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      fetchAll();
    }
  };

  // (duplicate moveOrigin definition removed by refactor)

  const moveFund = async (newOrder) => {
    setFunds(newOrder);
    try {
      await api.post(`/organizations/${activeOrgId}/funds/reorder`, {
        ordered_ids: newOrder.map((f) => f.id),
      });
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      fetchAll();
    }
  };

  const moveCategory = async (type, newOrder) => {
    const otherType = cats.filter((c) => c.type !== type);
    setCats([...otherType, ...newOrder]);
    try {
      await api.post(`/organizations/${activeOrgId}/categories/reorder`, {
        ordered_ids: newOrder.map((c) => c.id),
      });
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      fetchAll();
    }
  };

  const income = cats.filter((c) => c.type === "income");
  const expense = cats.filter((c) => c.type === "expense");

  // (duplicate moveOrigin definition removed by refactor)

  const Group = ({ title, items, accent, type }) => (
    <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
      <div className="px-6 py-5 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-serif text-2xl">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Drag rows to reorder.</p>
        </div>
        <Badge variant="outline" className={`${accent} rounded-full`}>{items.length}</Badge>
      </div>
      {items.length === 0 ? (
        <div className="px-6 py-10 text-center text-muted-foreground">No categories.</div>
      ) : (
        <SortableList
          items={items}
          onReorder={(newOrder) => moveCategory(type, newOrder)}
          dragHandleTestid={(c) => `drag-handle-category-${c.id}`}
          renderItem={(c, handle) => (
            <div data-testid={`category-row-${c.id}`} className="px-4 py-3 flex items-center justify-between bg-card border-b border-border last:border-0 hover:bg-secondary/40 group">
              <div className="flex items-center gap-2">
                {handle}
                <span className="text-foreground">{c.name}</span>
                {c.is_system && (
                  <Badge variant="outline" className="bg-secondary text-muted-foreground text-[10px] rounded-full border-0">Default</Badge>
                )}
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setDialogOpen(true); }} className="rounded-lg h-8 w-8 p-0" data-testid={`edit-category-${c.id}`} title="Rename">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmTarget(c)} className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-category-${c.id}`} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        />
      )}
    </Card>
  );

  return (
    <div className={embedded ? "space-y-6" : "space-y-8 fade-up"}>
      {!embedded && (
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Categories</div>
            <h1 className="font-serif text-5xl tracking-tight mt-2">Income & expense buckets</h1>
            <p className="text-muted-foreground mt-2 max-w-md">
              Pre-seeded with common church and nonprofit categories. Rename or remove any of them — they're yours to shape.
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="h-11 rounded-2xl soft-glow"
            data-testid="add-category-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Add category
          </Button>
        </header>
      )}
      {embedded && (
        <div className="flex items-center justify-end">
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="h-10 rounded-xl soft-glow"
            data-testid="add-category-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Add category
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Group title="Income" items={income} accent="bg-primary/10 text-primary border-primary/20" type="income" />
            <Group title="Expense" items={expense} accent="bg-expense/10 text-expense border-expense/20" type="expense" />
          </div>

          <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden mt-6">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-serif text-2xl">Funds</h3>
                <p className="text-xs text-muted-foreground mt-1">Designated giving buckets — General, Building, Missions, etc.</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-secondary text-muted-foreground rounded-full border-0">{funds.length}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openFundDialog(null)}
                  className="rounded-xl"
                  data-testid="add-fund-button"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add fund
                </Button>
              </div>
            </div>
            {funds.length === 0 ? (
              <div className="px-6 py-10 text-center text-muted-foreground">No funds.</div>
            ) : (
              <SortableList
                items={funds}
                onReorder={moveFund}
                dragHandleTestid={(f) => `drag-handle-fund-${f.id}`}
                renderItem={(f, handle) => (
                  <div data-testid={`fund-row-${f.id}`} className="px-4 py-3 flex items-center justify-between bg-card border-b border-border last:border-0 hover:bg-secondary/40 group">
                    <div className="flex items-center gap-2">
                      {handle}
                      <span>{f.name}</span>
                      {f.is_system && <Badge variant="outline" className="bg-secondary text-muted-foreground text-[10px] rounded-full border-0">Default</Badge>}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openFundDialog(f)} data-testid={`edit-fund-${f.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setConfirmFund(f)} data-testid={`delete-fund-${f.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              />
            )}
          </Card>

          <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden mt-6">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-serif text-2xl">Donation Origins</h3>
                <p className="text-xs text-muted-foreground mt-1">Where donations come from — Sunday Service, Online Giving, Special Events, etc.</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-secondary text-muted-foreground rounded-full border-0">{origins.length}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openOriginDialog(null)}
                  className="rounded-xl"
                  data-testid="add-origin-button"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add origin
                </Button>
              </div>
            </div>
            {origins.length === 0 ? (
              <div className="px-6 py-10 text-center text-muted-foreground">No origins.</div>
            ) : (
              <SortableList
                items={origins}
                onReorder={moveOrigin}
                dragHandleTestid={(o) => `drag-handle-origin-${o.id}`}
                renderItem={(o, handle) => (
                  <div data-testid={`origin-row-${o.id}`} className="px-4 py-3 flex items-center justify-between bg-card border-b border-border last:border-0 hover:bg-secondary/40 group">
                    <div className="flex items-center gap-2">
                      {handle}
                      <span>{o.name}</span>
                      {o.is_system && <Badge variant="outline" className="bg-secondary text-muted-foreground text-[10px] rounded-full border-0">Default</Badge>}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openOriginDialog(o)} data-testid={`edit-origin-${o.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setConfirmOrigin(o)} data-testid={`delete-origin-${o.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              />
            )}
          </Card>
        </>
      )}

      <CategoryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={activeOrgId}
        category={editing}
        onSaved={fetchAll}
      />

      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Delete "${confirmTarget?.name}"?`}
        description={
          confirmTarget?.is_system
            ? "This is a default category. If it's in use by any transaction or recurring rule, the delete will be blocked. Otherwise it will be removed permanently."
            : "If this category is in use by any transaction or recurring rule, the delete will be blocked. Otherwise it will be removed permanently."
        }
        confirmLabel="Delete category"
        onConfirm={doDelete}
        testid="confirm-delete-category"
      />

      <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
        <DialogContent data-testid="fund-dialog" className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingFund ? "Rename fund" : "New fund"}</DialogTitle>
            <DialogDescription>Funds designate where giving is allocated (General, Building, Missions, etc).</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Name</Label>
            <Input
              data-testid="fund-name-input"
              value={fundName}
              onChange={(e) => setFundName(e.target.value)}
              placeholder="e.g. Capital Campaign"
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFundDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={saveFund} className="rounded-xl" data-testid="fund-save-button">{editingFund ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmFund}
        onOpenChange={(o) => { if (!o) setConfirmFund(null); }}
        title={`Delete "${confirmFund?.name}"?`}
        description="If this fund is in use by any donation or recurring rule, the delete will be blocked. Otherwise it will be removed permanently."
        confirmLabel="Delete fund"
        onConfirm={doDeleteFund}
        testid="confirm-delete-fund"
      />

      <Dialog open={originDialogOpen} onOpenChange={setOriginDialogOpen}>
        <DialogContent data-testid="origin-dialog" className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingOrigin ? "Rename origin" : "New donation origin"}</DialogTitle>
            <DialogDescription>Origins tag where a donation came from (e.g. Sunday Service, Online Giving).</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Name</Label>
            <Input
              data-testid="origin-name-input"
              value={originName}
              onChange={(e) => setOriginName(e.target.value)}
              placeholder="e.g. Mid-week Service"
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOriginDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={saveOrigin} className="rounded-xl" data-testid="origin-save-button">{editingOrigin ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmOrigin}
        onOpenChange={(o) => { if (!o) setConfirmOrigin(null); }}
        title={`Delete "${confirmOrigin?.name}"?`}
        description="Donations already tagged with this origin will keep the text but lose the linked tag. This cannot be undone."
        confirmLabel="Delete origin"
        onConfirm={doDeleteOrigin}
        testid="confirm-delete-origin"
      />
    </div>
  );
}
